import React, { useState, useEffect } from 'react';
import { useScript } from '../context/ScriptContext';
import { Product } from '../types';
import * as api from '../services/api';
import { ApiError } from '../services/api';

interface OrderFormModalProps {
  product: Product;
  onClose: () => void;
}

const OrderFormModal: React.FC<OrderFormModalProps> = ({ product, onClose }) => {
  const { t } = useScript();
  const [form, setForm] = useState({
    customerName: '',
    phone: '',
    email: '',
    address: '',
    quantity: 1,
    note: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErr, setFieldErr] = useState<{ name?: string; phone?: string }>({});
  const [done, setDone] = useState(false);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const set = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errs: { name?: string; phone?: string } = {};
    if (!form.customerName.trim()) errs.name = t('Име је обавезно.');
    if (!form.phone.trim()) errs.phone = t('Телефон је обавезан.');
    setFieldErr(errs);
    if (errs.name || errs.phone) return;

    setSubmitting(true);
    setError('');
    try {
      await api.submitOrder({
        productId: product.id,
        customerName: form.customerName,
        phone: form.phone,
        email: form.email,
        address: form.address,
        quantity: Number(form.quantity) || 1,
        note: form.note,
      });
      setDone(true);
    } catch (err) {
      const apiErr = err as ApiError;
      setError(apiErr?.message || t('Грешка при слању. Покушајте поново.'));
      setSubmitting(false);
    }
  };

  return (
    <div className="order-overlay" onClick={onClose}>
      <div
        className="order-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('Наручи производ')}
        onClick={e => e.stopPropagation()}
      >
        <button className="order-close" onClick={onClose} aria-label={t('Затвори')}>✕</button>

        {done ? (
          <div className="order-success" role="status" aria-live="polite">
            <div className="order-success-icon" aria-hidden="true">{'✓'}</div>
            <h3>{t('Хвала!')}</h3>
            <p>{t('Ваша наруџбина је послата. Контактираћемо вас у најкраћем року.')}</p>
            <button className="btn-primary" onClick={onClose}>{t('Затвори')}</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <h3 className="order-title">{t('Наручивање')}</h3>
            <div className="order-product-line">
              <span className="order-product-name">{t(product.name)}</span>
              {product.price && <span className="order-product-price">{t(product.price)}</span>}
              {product.unit && <span className="order-product-unit">{t(product.unit)}</span>}
            </div>
            <p className="order-hint">{t('Без плаћања картицом — оставите контакт и договарамо се око испоруке.')}</p>

            {error && <div className="form-error-banner" role="alert">{error}</div>}

            <label className="post-admin-label">{t('Име и презиме')} *</label>
            <input
              className="news-edit-input"
              type="text"
              value={form.customerName}
              onChange={e => { if (fieldErr.name) setFieldErr(p => ({ ...p, name: undefined })); set('customerName')(e); }}
              aria-invalid={Boolean(fieldErr.name)}
            />
            {fieldErr.name && <div className="field-error" role="alert">{fieldErr.name}</div>}

            <label className="post-admin-label">{t('Телефон')} *</label>
            <input
              className="news-edit-input"
              type="tel"
              value={form.phone}
              onChange={e => { if (fieldErr.phone) setFieldErr(p => ({ ...p, phone: undefined })); set('phone')(e); }}
              aria-invalid={Boolean(fieldErr.phone)}
            />
            {fieldErr.phone && <div className="field-error" role="alert">{fieldErr.phone}</div>}

            <label className="post-admin-label">{t('Имејл')}</label>
            <input className="news-edit-input" type="email" value={form.email} onChange={set('email')} />

            <label className="post-admin-label">{t('Адреса за испоруку')}</label>
            <input className="news-edit-input" type="text" value={form.address} onChange={set('address')} />

            <label className="post-admin-label">{t('Количина')}</label>
            <input
              className="news-edit-input order-qty"
              type="number"
              min={1}
              value={form.quantity}
              onChange={e => setForm(prev => ({ ...prev, quantity: Math.max(1, Number(e.target.value) || 1) }))}
            />

            <label className="post-admin-label">{t('Напомена')}</label>
            <textarea className="news-edit-textarea" rows={3} value={form.note} onChange={set('note')} />

            <div className="order-actions">
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? t('Слање...') : t('Пошаљи наруџбину')}
              </button>
              <button type="button" className="btn-secondary" onClick={onClose}>{t('Откажи')}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default OrderFormModal;
